import type { ComponentPropsWithRef, ReactNode } from 'react'

import { cn } from '../../lib/cn'
import s from './ToggleChip.module.css'

/**
 * Puce à bascule — le `.chip` du prototype back-office (`mocks/ghp-bo/app.css`) : les communes du
 * secteur d'une équipe, les modules d'un rôle. Ni `Chip` (la puce d'équipe teintée) ni `Tag`
 * (l'étiquette) : celle-ci **s'allume**, et c'est son seul état.
 *
 * Pas de primitive : un `<button aria-pressed>` porte déjà le rôle, le focus, l'activation au
 * clavier et l'état annoncé (règle 3 du design system, comme `Button`).
 *
 * **Sans `onPressedChange`, c'est une étiquette** (`<span>`), pas un bouton désactivé : une puce
 * qui dit un état qu'on ne change pas d'ici — les modules que le rôle ouvre — n'a rien à promettre
 * au clic, et un bouton grisé ferait chercher pourquoi il l'est.
 */
export interface ToggleChipProps extends Omit<ComponentPropsWithRef<'button'>, 'onChange'> {
  pressed: boolean
  onPressedChange?: (pressed: boolean) => void
  /** Icône de tête, en slot — 15px au prototype. */
  icon?: ReactNode
  /** Le décompte en mono du prototype (`.chip .count`). */
  count?: number
}

export function ToggleChip({
  pressed,
  onPressedChange,
  icon,
  count,
  className,
  children,
  ...props
}: ToggleChipProps) {
  const content = (
    <>
      {icon}
      {children}
      {count !== undefined && <span className={s.count}>{count}</span>}
    </>
  )

  if (onPressedChange === undefined) {
    return (
      <span className={cn(s.root, s.static, className)} data-on={pressed ? '' : undefined}>
        {content}
      </span>
    )
  }

  return (
    <button
      type="button"
      className={cn(s.root, className)}
      aria-pressed={pressed}
      data-on={pressed ? '' : undefined}
      onClick={() => onPressedChange(!pressed)}
      {...props}
    >
      {content}
    </button>
  )
}

/** La rangée de puces — `.chips` du prototype : `gap:8px`, qui passe à la ligne. */
export function ToggleChipGroup({ className, ...props }: ComponentPropsWithRef<'div'>) {
  return <div role="group" className={cn(s.group, className)} {...props} />
}
